// ─────────────────────────────────────────────────────────────────────────────
// Customer Email Models
// ─────────────────────────────────────────────────────────────────────────────
// Interfaces and types for the customer emails registry.
// Matches backend API response shapes from /v1/company-emails endpoints.
// Source discriminator uses 'erp' | 'volta' (renamed 2026-05-22 to match
// backend parity; see .planning/phases/24-frontend-wiring/24-CONTEXT.md D-01).
// ─────────────────────────────────────────────────────────────────────────────

// ── Types ────────────────────────────────────────────────────────────────────

export type CompanyEmailSource = 'erp' | 'volta';

// ── Interfaces ───────────────────────────────────────────────────────────────

export interface ICompanyEmail {
  id: number;
  email: string;
  source: CompanyEmailSource;
  customer_id: number;
  customer_name: string | null;
  create_date: string | null;
  /** Present on location-scoped rows (/v1/location-emails); the res.partner id. */
  location_id?: number;
}

export interface ICreateCustomerEmailPayload {
  customer_id: number;
  email: string;
}

/**
 * Bulk-create payload for POST /v1/company-emails/bulk. Adds several addresses
 * to one company's registry in a single request; the endpoint is idempotent —
 * addresses already registered are skipped server-side, never errored.
 */
export interface ICreateCustomerEmailsBulkPayload {
  customer_id: number;
  emails: string[];
}

/**
 * Single-create payload for POST /v1/location-emails. Scopes an address to a
 * specific location (res.partner) instead of the whole company.
 */
export interface ICreateLocationEmailPayload {
  location_id: number;
  email: string;
}

/**
 * Bulk-create payload for POST /v1/location-emails/bulk. The location-scoped
 * sibling of ICreateCustomerEmailsBulkPayload; idempotent on duplicates.
 */
export interface ICreateLocationEmailsBulkPayload {
  location_id: number;
  emails: string[];
}

export interface IUpdateCustomerEmailPayload {
  email: string;
}

/**
 * Typed error the service emits from an HttpErrorResponse. The service maps
 * backend status codes to a `code` sentinel (404 → EMAIL_NOT_FOUND, 409 →
 * DUPLICATE) so the effect can render the right localized toast without
 * re-inspecting raw HTTP shape.
 */
export interface IStructuredApiError {
  code?: string;
  message: string;
}

// ── Constants ────────────────────────────────────────────────────────────────

/**
 * FE-side sentinel error codes the service emits via `IStructuredApiError.code`.
 * EMAIL_NOT_FOUND is the FE translation of a backend HTTP 404 on PATCH/DELETE —
 * happens when the row was either already deleted by someone else, or was
 * actually ERP-sourced (backend returns 404 not 403 by design — Phase 21 D-14).
 */
export const STRUCTURED_ERROR_CODES = {
  EMAIL_NOT_FOUND: 'EMAIL_NOT_FOUND',
  DUPLICATE: 'duplicate',
  INVALID_EMAIL: 'invalid_email',
} as const;
